/**
 * Adapter that maps credit-store state to a single declarative config the
 * modal renders. Three variants share the same frame (title, description,
 * icon, progress bar, reset label, actions) but differ in copy, palette, and
 * which CTA block renders.
 *
 * Pure-function shape makes each variant individually unit-testable without
 * mounting the dialog.
 */
import { useCreditStore, useUsagePercent, formatCredits } from "~/stores/creditStore";

export type CreditModalKey = "trial-exhausted" | "plan-exhausted" | "low-credits";

export interface CreditModalPalette {
  circleBg: string;
  iconFill: string;
  barGradient: string;
}

export interface CreditModalVariant {
  key: CreditModalKey;
  title: string;
  description: string;
  palette: CreditModalPalette;
  resetLabel: string | null;
  progressPercent: number;
  balanceDisplay: string;
  dismiss: () => void;
}

const EXHAUSTED_PALETTE: CreditModalPalette = {
  circleBg: "bg-[#fee9e4]",
  iconFill: "#8B3523",
  barGradient: "bg-gradient-to-r from-[#ea9e86] to-[#ba543e]",
};

const LOW_PALETTE: CreditModalPalette = {
  circleBg: "bg-[#faecdb]",
  iconFill: "#BF873F",
  barGradient: "bg-gradient-to-r from-[#f2d0a5] to-[#e5a24c]",
};

function formatShortDate(iso: string): string {
  return new Date(iso).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

// Trial-exhausted omits the reset label entirely — trial grants do not renew.
function resetLabelFor(
  key: CreditModalKey,
  isTrialing: boolean,
  trialEndsAt: string | null,
  periodEndsAt: string | null,
): string | null {
  if (key === "trial-exhausted") return null;
  if (isTrialing && trialEndsAt) return `Expires on ${formatShortDate(trialEndsAt)}`;
  if (periodEndsAt) return `Resets on ${formatShortDate(periodEndsAt)}`;
  return null;
}

export function useCreditModalVariant(): CreditModalVariant | null {
  const showOutOfCredits = useCreditStore((s) => s.showOutOfCreditsModal);
  const showLowCredit = useCreditStore((s) => s.showLowCreditModal);
  const balance = useCreditStore((s) => s.balance);
  const planCreditsAllocated = useCreditStore((s) => s.planCreditsAllocated);
  const periodEndsAt = useCreditStore((s) => s.periodEndsAt);
  const isTrialing = useCreditStore((s) => s.isTrialing);
  const trialEndsAt = useCreditStore((s) => s.trialEndsAt);
  const dismissModal = useCreditStore((s) => s.dismissModal);
  const dismissLowCreditWarning = useCreditStore((s) => s.dismissLowCreditWarning);
  const usagePercent = useUsagePercent();

  if (!showOutOfCredits && !showLowCredit) return null;

  // Exhausted takes priority when both flags happen to be true.
  const key: CreditModalKey = showOutOfCredits
    ? isTrialing ? "trial-exhausted" : "plan-exhausted"
    : "low-credits";

  const balanceDisplay = `${formatCredits(balance)}/${
    planCreditsAllocated !== null ? formatCredits(planCreditsAllocated) : "\u2014"
  }`;

  switch (key) {
    case "trial-exhausted":
      return {
        key,
        title: "You\u2019re out of trial credits",
        description:
          "You've used your trial evaluation credits. Upgrade now to keep building!",
        palette: EXHAUSTED_PALETTE,
        resetLabel: null,
        progressPercent: usagePercent ?? 0,
        balanceDisplay,
        dismiss: dismissModal,
      };
    case "plan-exhausted":
      return {
        key,
        title: "You\u2019ve reached your credit limit",
        description:
          "You\u2019ve used all your credits for this billing period. Purchase more or upgrade your plan to continue using AI features.",
        palette: EXHAUSTED_PALETTE,
        resetLabel: resetLabelFor(key, isTrialing, trialEndsAt, periodEndsAt),
        progressPercent: usagePercent ?? 0,
        balanceDisplay,
        dismiss: dismissModal,
      };
    case "low-credits":
      return {
        key,
        title: "You\u2019re running low on credits",
        description: `You\u2019ve used ${usagePercent ?? 0}% of your monthly credits. Purchase more or upgrade your plan to keep creating.`,
        palette: LOW_PALETTE,
        resetLabel: resetLabelFor(key, isTrialing, trialEndsAt, periodEndsAt),
        progressPercent: usagePercent ?? 0,
        balanceDisplay,
        dismiss: dismissLowCreditWarning,
      };
  }
}
